import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DialogResult, PopupDialog } from '@app/Common';
import { ProvisionedApplicationsTypes } from '@app/Enums';
import { CreateTenantDto } from '@app/Models/create-tenant.dto';
import { Tenant } from '@app/Models/tenant';
import { TenantEnvironmentDetails } from '@app/Models/tenant-environment-details.model';
import { TenantService } from '@app/Services';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/internal/operators/filter';
import { takeUntil, switchMap } from 'rxjs/operators';
import { CloneService } from '../../Services';
import { AsyncPipe } from '@angular/common';
import { JobStatusPlaceholderComponent } from '../../../../../Common/job-status-placeholder/job-status-placeholder.component';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { NewTenantFormComponent } from '../../../../Components/NewTenantForm/new-tenant-form.component';

@Component({
    selector: 'app-clone-export-import',
    templateUrl: './clone-export-import.component.html',
    styleUrls: [
        './clone-export-import.component.scss'
    ],
    imports: [JobStatusPlaceholderComponent, MatButton, FormsModule, MatSelect, MatOption, MatCheckbox, NewTenantFormComponent, AsyncPipe]
})
export class CloneExportImportComponent implements OnInit, OnDestroy {
  cloneInProgress$: Observable<boolean>;
  cloneFinalised$: Observable<boolean>;
  cloneFromTenantValue: string;
  cloneToTenantValue: string;
  cloneFromTenant: Tenant;
  cloneToTenant: Tenant;
  cloneFromTenant$: Observable<Tenant>;
  cloneToTenant$: Observable<Tenant>;
  cloneToTenantOptions$: Observable<Tenant[]>;
  cloneFromEnvironment$: Observable<TenantEnvironmentDetails>;
  showProgressBar: boolean = false;
  cloneRecords: boolean = false;

  private isDestroyed$ = new Subject<void>();

  constructor(
    private tenantService: TenantService,
    private cloneService: CloneService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.cloneInProgress$ = this.cloneService.cloneInProgress$;
    this.cloneFinalised$ = this.cloneService.cloneFinalised$;
    this.cloneFromTenant$ = this.cloneService.sourceTenant$;
  }

  ngOnInit(): void {
    this.changeCloneFrom();
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
  }

  cloneAnotherTenant(): void {
    location.reload();
  }

  redirectToClonedTenant(): void {
    const tenantId = this.cloneService.getNewTenant()?.TenantId;
    if (tenantId) {
      this.router.navigate([`/tenant/${tenantId}/`]);
    }
  }

  getTenantList(): Tenant[] {
    return this.tenantService.tenants.filter((tenant) => tenant.HasAccess &&
      tenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)
    );
  }

  changeCloneFrom(): void {
    if (!this.cloneFromTenantValue) {
      return;
    }
    this.cloneFromTenant = this.tenantService.tenants.find((tenant) =>
      tenant.Value === this.cloneFromTenantValue
    );
    this.cloneFromEnvironment$ = this.getEnvironmentForTenant(
      this.cloneFromTenantValue
    );
  }

  openCloneConfirmationPopup(createTenantDto: CreateTenantDto): void {
    const newTenant: Tenant = {
      ...createTenantDto
    } as Tenant;

    const dialogRef = this.createConfirmationPopup(createTenantDto);
    dialogRef.afterClosed()
      .pipe(
        filter((result) => !!result && result.Result),
        switchMap(() => this.cloneFromEnvironment$),
        switchMap((cloneFromEnvironment) =>
          this.cloneService.cloneExportImport(this.cloneFromTenant, cloneFromEnvironment.EnvironmentId, newTenant, this.cloneRecords)
        ),
        takeUntil(this.isDestroyed$)
      )
      .subscribe();
  }

  private createConfirmationPopup(tenant: CreateTenantDto): MatDialogRef<PopupDialog, DialogResult> {
    const dialogRef = this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
    dialogRef.componentInstance.DialogTitle = 'Clone Tenant';
    dialogRef.componentInstance.DialogDescription =
      `Are you sure you want to Clone tenant <b>${this.cloneFromTenant.Value}</b> into the new tenant <b>${tenant.Text}</b>?`;
    dialogRef.componentInstance.DialogConfirmButtonText = 'Confirm';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    return dialogRef;
  }

  private getEnvironmentForTenant(tenantName: string): Observable<TenantEnvironmentDetails> {
    return this.tenantService
      .getEnvironmentDetailsByTenant(tenantName)
      .pipe(
        filter((environemntDetails: TenantEnvironmentDetails | null) => environemntDetails !== null)
      );
  }
}
